import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Filter, ArrowDown, ArrowUp, Download } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, apiBaseUrl } from "@/lib/api";
import {
    SACard, StatusPill, formatBytes, timeAgo,
} from "@/components/superadmin/SAPrimitives";
import SATenantActions from "@/components/superadmin/SATenantActions";

const SORT_OPTIONS = [
    { value: "last_activity", label: "Last Activity" },
    { value: "created_at", label: "Created" },
    { value: "name", label: "Name" },
    { value: "agents", label: "Agents" },
    { value: "users", label: "Users" },
];

export default function SuperAdminTenants() {
    const [params, setParams] = useSearchParams();
    const q = params.get("q") || "";
    const status = params.get("status") || "all";
    const plan = params.get("plan") || "";
    const sort = params.get("sort") || "last_activity";
    const order = params.get("order") || "desc";
    const includeDeleted = params.get("include_deleted") === "1";

    const [tenants, setTenants] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/superadmin/tenants", {
                params: {
                    q: q || undefined,
                    status,
                    plan: plan || undefined,
                    sort,
                    order,
                    include_deleted: includeDeleted,
                    limit: 300,
                },
            });
            setTenants(data.tenants || []);
            setTotal(data.total || 0);
            setErr("");
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setLoading(false);
        }
    }, [q, status, plan, sort, order, includeDeleted]);

    useEffect(() => { load(); }, [load]);

    const setParam = (k, v) => {
        const next = new URLSearchParams(params);
        if (v == null || v === "" || v === "all") next.delete(k);
        else next.set(k, v);
        setParams(next, { replace: true });
    };

    const onAction = async (tenant, action, payload = {}) => {
        try {
            if (action === "delete") {
                await http.delete(`/superadmin/tenants/${tenant.id}`, { data: payload });
                toast.success(`${tenant.name} scheduled for purge in 30 days`);
            } else if (action === "suspend") {
                await http.post(`/superadmin/tenants/${tenant.id}/suspend`, payload);
                toast.success(`${tenant.name} suspended`);
            } else if (action === "activate") {
                await http.post(`/superadmin/tenants/${tenant.id}/activate`, payload);
                toast.success(`${tenant.name} activated`);
            } else if (action === "restore") {
                await http.post(`/superadmin/tenants/${tenant.id}/restore`, payload);
                toast.success(`${tenant.name} restored`);
            }
            load();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    return (
        <div className="space-y-5" data-testid="sa-tenants-page">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="text-[24px] font-semibold tracking-tight">Tenants</h1>
                    <div className="mt-1 text-[12px] text-white/55">
                        {total} workspace{total === 1 ? "" : "s"} · {status === "all" ? "all statuses" : status}
                    </div>
                </div>
                <a
                    data-testid="sa-tenants-export-csv"
                    href={`${apiBaseUrl()}/api/superadmin/tenants.csv${
                        includeDeleted ? "?include_deleted=true" : ""
                    }`}
                    download
                    className="flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.02] px-3 py-1.5 text-[12px] text-white/80 transition-colors hover:border-[#00C2FF]/40 hover:text-[#00C2FF]"
                >
                    <Download size={12} /> Export CSV
                </a>
            </div>

            {/* Toolbar */}
            <SACard className="flex flex-wrap items-center gap-2 p-3">
                <div className="relative min-w-[220px] flex-1">
                    <Search
                        size={13}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35"
                    />
                    <input
                        data-testid="sa-tenants-search"
                        key={q}
                        defaultValue={q}
                        placeholder="Search name, slug, license ref (MB-…), or member email…"
                        onChange={(e) => {
                            // Debounced URL-param update → refetch via useEffect.
                            const v = e.currentTarget.value.trim();
                            clearTimeout(window.__saSearchT);
                            window.__saSearchT = setTimeout(() => setParam("q", v), 250);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                clearTimeout(window.__saSearchT);
                                setParam("q", e.currentTarget.value.trim());
                            }
                        }}
                        className="h-8 w-full rounded-md border border-white/5 bg-white/[0.03] pl-8 pr-3 text-[12.5px] text-white placeholder:text-white/30 focus:border-[#00C2FF]/50 focus:outline-none"
                    />
                </div>
                <select
                    data-testid="sa-tenants-status"
                    value={status}
                    onChange={(e) => setParam("status", e.target.value)}
                    className="h-8 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[12px] text-white focus:outline-none"
                >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                </select>
                <select
                    data-testid="sa-tenants-sort"
                    value={sort}
                    onChange={(e) => setParam("sort", e.target.value)}
                    className="h-8 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[12px] text-white focus:outline-none"
                >
                    {SORT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>Sort: {o.label}</option>
                    ))}
                </select>
                <button
                    data-testid="sa-tenants-order"
                    onClick={() => setParam("order", order === "desc" ? "asc" : "desc")}
                    className="flex h-8 items-center gap-1 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[12px] text-white/75 hover:text-white"
                >
                    {order === "desc" ? <ArrowDown size={12} /> : <ArrowUp size={12} />} {order}
                </button>
                <label className="flex h-8 items-center gap-1.5 rounded-md border border-white/5 bg-white/[0.03] px-2 text-[11.5px] text-white/65">
                    <input
                        type="checkbox"
                        data-testid="sa-tenants-include-deleted"
                        checked={includeDeleted}
                        onChange={(e) => setParam("include_deleted", e.target.checked ? "1" : "")}
                        className="accent-[#00C2FF]"
                    />
                    Include deleted
                </label>
            </SACard>

            {err && (
                <SACard className="text-red-300" data-testid="sa-tenants-error">
                    {err}
                </SACard>
            )}

            <SACard className="p-0">
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead>
                            <tr className="border-b border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                                <th className="px-5 py-2.5 font-medium">Company</th>
                                <th className="px-2 py-2.5 font-medium">Plan</th>
                                <th className="px-2 py-2.5 font-medium">Status</th>
                                <th className="px-2 py-2.5 text-right font-medium">Users</th>
                                <th className="px-2 py-2.5 text-right font-medium">Agents</th>
                                <th className="px-2 py-2.5 text-right font-medium">Devices</th>
                                <th className="px-2 py-2.5 text-right font-medium">Data (30d)</th>
                                <th className="px-2 py-2.5 text-right font-medium">Active Jobs</th>
                                <th className="px-2 py-2.5 font-medium">Last Activity</th>
                                <th className="px-5 py-2.5 text-right font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tenants.map((t) => (
                                <tr
                                    key={t.id}
                                    className="border-b border-white/5 transition-colors hover:bg-white/[0.03]"
                                    data-testid={`sa-tenants-row-${t.slug || t.id}`}
                                >
                                    <td className="px-5 py-3">
                                        <Link
                                            to={`/superadmin/tenants/${t.id}`}
                                            className="font-semibold text-white hover:text-[#00C2FF]"
                                        >
                                            {t.name}
                                        </Link>
                                        <div className="text-[10.5px] text-white/45">
                                            {t.slug || "—"}
                                        </div>
                                    </td>
                                    <td className="px-2 py-3 uppercase text-white/70">{t.plan || "free"}</td>
                                    <td className="px-2 py-3">
                                        <StatusPill status={t.deleted_at ? (t.status === "purged" ? "purged" : "deleted") : t.status || "active"} />
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">{t.users_count}</td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {t.online_agents_count}/{t.agents_count}
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {t.active_devices_count}
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">
                                        {formatBytes(t.bytes_transferred_30d)}
                                    </td>
                                    <td className="px-2 py-3 text-right text-white/80">{t.active_jobs}</td>
                                    <td className="px-2 py-3 text-white/65">{timeAgo(t.last_activity)}</td>
                                    <td className="px-5 py-3 text-right">
                                        <SATenantActions
                                            tenant={t}
                                            onAction={(action, payload) => onAction(t, action, payload)}
                                        />
                                    </td>
                                </tr>
                            ))}
                            {!loading && tenants.length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-5 py-12 text-center text-white/40">
                                        No tenants match.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </SACard>
        </div>
    );
}
